/**
 * Poseidon dashboard — confirmation UX for every §3.1 control.
 *
 * §3.1 requires explicit confirmation before: enabling/disabling demo execution,
 * approving/rejecting an order intent, cancelling an active order, closing a
 * position, activating soft/hard halt, and modifying risk limits or allow lists.
 *
 * Design rules enforced here:
 *   - the operator must TYPE a phrase; a single click is never enough;
 *   - the consequence of the action is spelled out before it is possible to
 *     confirm it;
 *   - soft halt and hard halt are visually distinct (amber vs pulsing red) and
 *     the hard-halt dialog also states what remains possible;
 *   - the dialog is a safeguard for the operator, not a security control: the
 *     daemon re-verifies the phrase, the operator's authority and the risk state.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { CommandKind, CommandReceipt } from '../api/types';
import { cx } from '../lib/cx';
import { useCommand } from '../hooks/useCommand';
import type { ApiError } from '../api/client';

export type ConfirmSeverity = 'normal' | 'destructive' | 'soft-halt' | 'hard-halt';

const SEVERITY_LABEL: Record<ConfirmSeverity, string> = {
  normal: 'Confirm action',
  destructive: 'Confirm destructive action',
  'soft-halt': 'Confirm SOFT HALT',
  'hard-halt': 'Confirm HARD HALT',
};

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  /** Bullet list of what will actually happen. Required for destructive actions. */
  consequences?: readonly string[];
  severity?: ConfirmSeverity;
  /** The exact phrase the operator must type. Shown, never hidden. */
  confirmationPhrase: string;
  requireReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  confirmLabel?: string;
  busy?: boolean;
  /** Error from the daemon or the network, surfaced verbatim. */
  error?: ApiError | null;
  onConfirm: (input: { confirmation: string; reason: string | null }) => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  consequences,
  severity = 'normal',
  confirmationPhrase,
  requireReason = false,
  reasonLabel = 'Reason (recorded in the audit log)',
  reasonPlaceholder = 'Why is this change being made?',
  confirmLabel = 'Confirm',
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element | null {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) {
      setTyped('');
      setReason('');
      return;
    }
    // Focus the confirmation field so a keyboard-only operator can proceed.
    const timer = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const phraseMatches = typed.trim() === confirmationPhrase;
  const reasonOk = !requireReason || reason.trim().length >= 8;

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !busy && onCancel()}>
      <div
        className={cx('dialog', severity === 'soft-halt' && 'dialog--soft', severity === 'hard-halt' && 'dialog--hard')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="dialog__head">
          <h2 className="dialog__title" id={titleId}>
            {title}
          </h2>
          <span className={cx('badge', severity === 'hard-halt' ? 'tone-danger' : severity === 'soft-halt' ? 'tone-warn' : severity === 'destructive' ? 'tone-danger' : 'tone-neutral')}>
            {SEVERITY_LABEL[severity]}
          </span>
        </header>

        <div className="dialog__body">
          <div>{description}</div>

          {consequences !== undefined && consequences.length > 0 && (
            <div>
              <div className="ribbon__label" style={{ marginBottom: 5 }}>
                What will happen
              </div>
              <ul className="consequences">
                {consequences.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <div className="ribbon__label" style={{ marginBottom: 5 }}>
              Type this phrase to confirm
            </div>
            <div className="dialog__confirm-phrase">{confirmationPhrase}</div>
          </div>

          <label>
            Confirmation
            <input
              ref={inputRef}
              type="text"
              value={typed}
              spellCheck={false}
              autoComplete="off"
              placeholder={confirmationPhrase}
              onChange={(event) => setTyped(event.target.value)}
              aria-invalid={typed.length > 0 && !phraseMatches}
            />
          </label>

          {requireReason && (
            <label>
              {reasonLabel}
              <textarea
                rows={3}
                value={reason}
                placeholder={reasonPlaceholder}
                onChange={(event) => setReason(event.target.value)}
              />
              {!reasonOk && <span className="text-warn">A short justification is required and is stored with the audit record.</span>}
            </label>
          )}

          {severity === 'hard-halt' && (
            <div className="disclosure" style={{ borderColor: '#8b2323' }}>
              A hard halt blocks every submission except the explicit emergency-close workflow (§7.3). Protective exits and
              cancels remain available. Releasing it is a separate confirmation.
            </div>
          )}

          {error !== null && (
            <div className="dialog__error" role="alert">
              <strong>{error.displayMessage}</strong>
              <div className="text-mono" style={{ fontSize: 11 }}>
                {error.code}
                {error.fields.length > 0 && <> · {error.fields.map((field) => `${field.field}: ${field.message}`).join('; ')}</>}
              </div>
            </div>
          )}
        </div>

        <footer className="dialog__foot">
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={cx('btn', severity === 'hard-halt' || severity === 'destructive' ? 'btn--danger' : severity === 'soft-halt' ? 'btn--warn' : 'btn--primary')}
            disabled={!phraseMatches || !reasonOk || busy}
            onClick={() => onConfirm({ confirmation: typed.trim(), reason: requireReason ? reason.trim() : reason.trim().length > 0 ? reason.trim() : null })}
          >
            {busy ? 'Submitting…' : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * A button that cannot act without a typed confirmation, wired to a command.
 * Used for every §3.1 control so the safety behaviour cannot drift per control.
 */
export interface ConfirmableActionProps {
  label: string;
  title: string;
  description: ReactNode;
  consequences: readonly string[];
  severity?: ConfirmSeverity;
  command: CommandKind;
  payload: unknown;
  confirmationPhrase: string;
  requireReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  confirmLabel?: string;
  buttonClassName?: string;
  disabled?: boolean;
  disabledReason?: string | null;
  onCompleted?: (receipt: CommandReceipt) => void;
}

export function ConfirmableAction({
  label,
  title,
  description,
  consequences,
  severity = 'normal',
  command,
  payload,
  confirmationPhrase,
  requireReason = false,
  reasonLabel,
  reasonPlaceholder,
  confirmLabel,
  buttonClassName = 'btn btn--sm',
  disabled = false,
  disabledReason = null,
  onCompleted,
}: ConfirmableActionProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const commandState = useCommand((receipt) => {
    setOpen(false);
    onCompleted?.(receipt);
  });

  const feedback = commandState.receipt !== null ? commandState.receipt.message : null;

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
      <button
        type="button"
        className={buttonClassName}
        disabled={disabled}
        title={disabled ? disabledReason ?? 'This action is unavailable.' : title}
        onClick={() => {
          commandState.reset();
          setOpen(true);
        }}
      >
        {label}
      </button>

      {disabled && disabledReason !== null && <span className="text-faint" style={{ fontSize: 11 }}>{disabledReason}</span>}
      {feedback !== null && (
        <span className={commandState.receipt?.accepted === true ? 'text-ok' : 'text-warn'} style={{ fontSize: 11 }}>
          {feedback}
        </span>
      )}
      {commandState.error !== null && (
        <span className="text-danger" style={{ fontSize: 11 }}>
          {commandState.error.displayMessage}
        </span>
      )}

      <ConfirmDialog
        open={open}
        title={title}
        description={description}
        consequences={consequences}
        severity={severity}
        confirmationPhrase={confirmationPhrase}
        requireReason={requireReason}
        reasonLabel={reasonLabel}
        reasonPlaceholder={reasonPlaceholder}
        confirmLabel={confirmLabel}
        busy={commandState.busy}
        error={commandState.error}
        onCancel={() => setOpen(false)}
        onConfirm={({ confirmation, reason }) => {
          void commandState.run(command, payload, { confirmation, reason });
        }}
      />
    </span>
  );
}
